import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Globe, Plus, Sparkles, CalendarRange, Users, Briefcase, IdCard, ClipboardList, SlidersHorizontal, Home, CalendarPlus, Timer, CheckCircle2, BellRing, Wallet, NotebookPen, Brain, Route, BookOpen, Activity, FolderArchive, Library, QrCode, ChartLine } from "lucide-react";
import { PhoneCarousel } from "./PhoneCarousel";
import { DetailList } from "./FeaturesSection";
import { Footer } from "./Footer";

const glowCycle = ["rgba(13,148,136,0.18)", "rgba(217,119,6,0.12)", "rgba(20,119,141,0.15)"];

const FEATURE_ICONS = {
    "01": Home, "02": Users, "03": CalendarPlus, "04": Timer, "05": CheckCircle2,
    "06": BellRing, "07": Wallet, "08": NotebookPen, "09": Sparkles, "10": Brain,
    "11": Route, "12": BookOpen, "13": Activity, "14": FolderArchive, "15": Library,
    "16": IdCard, "17": QrCode, "18": ClipboardList, "19": ChartLine, "20": SlidersHorizontal,
};

const CATEGORY_ICONS = {
    org: CalendarRange,
    clients: Users,
    ai: Sparkles,
    tools: Briefcase,
    profile: IdCard,
    surveys: ClipboardList,
    settings: SlidersHorizontal,
};

const FeatureIcon = ({ num, active }) => {
    const Icon = FEATURE_ICONS[num] || Sparkles;
    return (
        <span
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border transition-colors duration-300 ${
                active ? "border-[#0D9488]/40 bg-[#0D9488]/10 text-[#0D9488]" : "border-[#E2E8F0] bg-[#F8FAFC] text-[#64748B]"
            }`}
        >
            <Icon size={16} />
        </span>
    );
};

const GroupTitle = ({ id, title, className = "" }) => {
    const Icon = CATEGORY_ICONS[id] || Sparkles;
    return (
        <h2 className={`flex items-center gap-3 ${className}`}>
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-[#D97706]/25 bg-[#FEF3C7] text-[#D97706]">
                <Icon size={15} />
            </span>
            <span className="font-display text-xl italic text-[#0E4B57] md:text-2xl">{title}</span>
        </h2>
    );
};

export const SubHeader = ({ t, lang, onLangChange }) => (
    <header
        className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#032A44]/85 backdrop-blur-xl"
        data-testid="showcase-header"
    >
        <div className="mx-auto flex h-[84px] max-w-[1400px] items-center justify-between px-6 md:px-12">
            <a href="/" className="flex items-center gap-3.5" data-testid="showcase-home-link" aria-label="TheraOffice home">
                <img src="/brand/app-icon-512.webp" alt="TheraOffice logo" className="h-12 w-12 rounded-xl object-cover" />
                <img src="/brand/wordmark-white.webp" alt="TheraOffice" className="h-7 w-auto md:h-8" data-testid="showcase-wordmark" />
            </a>
            <div className="flex items-center gap-3">
                <button
                    onClick={() => onLangChange(lang === "el" ? "en" : "el")}
                    className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-bold tracking-widest text-[#A3B3B8] transition-colors duration-300 hover:border-[#4CC9DC] hover:text-white"
                    data-testid="showcase-lang-toggle"
                    aria-label="Switch language"
                >
                    <Globe size={14} />
                    {lang === "el" ? "EN" : "ΕΛ"}
                </button>
                <a
                    href="/#download"
                    className="hidden items-center gap-2 rounded-full bg-[#D97706] px-5 py-2.5 text-sm font-bold text-white shadow-[0_8px_24px_rgba(217,119,6,0.35)] transition-colors duration-300 hover:bg-[#B45309] sm:flex"
                    data-testid="showcase-download-button"
                >
                    {t.nav.cta}
                </a>
            </div>
        </div>
    </header>
);

export const Chip = ({ active, onClick, children, testId }) => (
    <button
        onClick={onClick}
        aria-pressed={active}
        className={`rounded-full border px-5 py-2.5 text-sm font-semibold transition-colors duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488] ${
            active
                ? "border-transparent bg-[#0D9488] text-white shadow-[0_8px_24px_rgba(13,148,136,0.3)]"
                : "border-[#CBD5E1] bg-white text-[#475569] hover:border-[#0D9488]/60 hover:text-[#0F172A]"
        }`}
        data-testid={testId}
    >
        {children}
    </button>
);

const Panel = ({ item, t, index }) => (
    <motion.div
        key={item.num}
        initial={{ opacity: 0, y: 26 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -14 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="rounded-[2rem] border border-[#E2E8F0] bg-white p-8 shadow-sm md:p-12"
        data-testid="showcase-panel"
    >
        <div className="flex items-baseline gap-4">
            <span className="font-display text-4xl italic text-[#D97706]">{item.num}</span>
            <h3 className="text-xs font-bold uppercase tracking-[0.22em] text-[#0D9488]">{item.title}</h3>
        </div>
        <p className="mt-5 max-w-xl text-base leading-relaxed text-[#1E293B] md:text-lg">{item.short}</p>
        <DetailList item={item} f={t.features} />
        <div className="mt-10 flex justify-center">
            <PhoneCarousel
                key={`showcase-${item.num}`}
                imgs={item.imgs}
                alt={item.alt}
                contain
                glow={glowCycle[index % glowCycle.length]}
                testId="showcase-phone"
            />
        </div>
    </motion.div>
);

export const FeatureShowcasePage = ({ t, lang, onLangChange }) => {
    const s = t.showcase;
    const all = t.features.list;
    const byNum = (n) => all.find((f) => f.num === n);

    const [catId, setCatId] = useState("all");
    const [active, setActive] = useState(0);
    const [openNum, setOpenNum] = useState(null);

    useEffect(() => {
        document.title = s.metaTitle;
        const desc = document.querySelector('meta[name="description"]');
        if (desc) desc.setAttribute("content", s.metaDesc);
        const canonical = document.querySelector('link[rel="canonical"]');
        if (canonical) canonical.setAttribute("href", "https://theraoffice.gr/features");
        window.scrollTo(0, 0);
    }, [s.metaTitle, s.metaDesc]);

    useEffect(() => {
        setActive(0);
        setOpenNum(null);
    }, [catId]);

    const groups = useMemo(() => {
        if (catId === "all") {
            return s.categories.map((c) => ({
                id: c.id,
                title: c.title,
                items: (c.id === "ai" ? c.nums.filter((n) => n === "09") : c.nums).map(byNum).filter(Boolean),
            }));
        }
        const c = s.categories.find((x) => x.id === catId);
        return [{ id: c.id, title: c.title, items: c.nums.map(byNum).filter(Boolean) }];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [catId, s]);

    const flat = groups.flatMap((g) => g.items);
    const current = flat[Math.min(active, Math.max(flat.length - 1, 0))];

    return (
        <div data-testid="showcase-page">
            <SubHeader t={t} lang={lang} onLangChange={onLangChange} />
            <main className="pt-36 md:pt-44">
                <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                        className="relative max-w-3xl"
                    >
                        <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.26em] text-[#0D9488] md:text-sm">
                            <span className="h-px w-10 bg-[#0D9488]" />
                            {s.overline}
                        </p>
                        <h1 className="font-display mt-6 text-balance text-4xl leading-[1.12] tracking-tight text-[#0F172A] md:text-6xl" data-testid="showcase-title">
                            {(() => {
                                const [a, b] = s.title.split(" / ");
                                return (
                                    <>
                                        {a}
                                        <span className="text-[#94A3B8]"> / </span>
                                        <span className="italic text-[#0D9488]">{b}</span>
                                    </>
                                );
                            })()}
                        </h1>
                        <p className="mt-7 max-w-2xl text-base leading-[1.85] text-[#475569] md:text-lg" data-testid="showcase-subtitle">
                            {s.subtitle}
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-12 flex flex-wrap gap-2.5 border-t border-[#E2E8F0] pt-9"
                        role="tablist"
                        aria-label="Feature categories"
                        data-testid="showcase-categories"
                    >
                        <Chip active={catId === "all"} onClick={() => setCatId("all")} testId="category-all">
                            {s.allLabel}
                        </Chip>
                        {s.categories.map((c) => (
                            <Chip key={c.id} active={catId === c.id} onClick={() => setCatId(c.id)} testId={`category-${c.id}`}>
                                {c.label}
                            </Chip>
                        ))}
                    </motion.div>
                </div>

                <div className="mx-auto mt-12 max-w-[1400px] px-6 pb-28 md:px-12" data-testid="showcase-content">
                    <div className="hidden gap-14 lg:grid lg:grid-cols-12">
                        <div className="lg:col-span-5 xl:col-span-4">
                            <div className="sticky top-28 max-h-[74vh] space-y-2 overflow-y-auto pr-3">
                                {groups.map((g) => (
                                    <div key={g.title}>
                                        <GroupTitle id={g.id} title={g.title} className="mb-4 mt-9 first:mt-0" />
                                        {g.items.map((item) => {
                                            const idx = flat.indexOf(item);
                                            const isActive = current && current.num === item.num;
                                            return (
                                                <button
                                                    key={item.num}
                                                    onClick={() => setActive(idx)}
                                                    aria-selected={isActive}
                                                    className={`mb-2.5 flex w-full items-center gap-4 rounded-2xl border px-4 py-4 text-left transition-all duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488] ${
                                                        isActive
                                                            ? "border-[#0D9488]/50 bg-white shadow-[0_12px_36px_rgba(13,148,136,0.12)]"
                                                            : "border-[#E2E8F0] bg-white/70 hover:border-[#0D9488]/40 hover:bg-white"
                                                    }`}
                                                    data-testid={`showcase-nav-${item.num}`}
                                                >
                                                    <FeatureIcon num={item.num} active={isActive} />
                                                    <span className={`flex-1 text-sm font-bold md:text-base ${isActive ? "text-[#0F172A]" : "text-[#475569]"}`}>
                                                        {item.title}
                                                    </span>
                                                    <span className={`font-display text-sm italic ${isActive ? "text-[#D97706]" : "text-[#94A3B8]"}`}>
                                                        {item.num}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="lg:col-span-7 xl:col-span-8">
                            <AnimatePresence mode="wait">
                                {current && <Panel key={current.num} item={current} t={t} index={flat.indexOf(current)} />}
                            </AnimatePresence>
                        </div>
                    </div>

                    <div className="space-y-10 lg:hidden">
                        {groups.map((g) => (
                            <div key={g.title}>
                                <GroupTitle id={g.id} title={g.title} className="mb-5" />
                                <div className="space-y-3">
                                    {g.items.map((item, gi) => {
                                        const isOpen = openNum === item.num;
                                        return (
                                            <div
                                                key={item.num}
                                                className={`overflow-hidden rounded-2xl border transition-all duration-300 ${
                                                    isOpen
                                                        ? "border-[#0D9488]/50 bg-white shadow-[0_16px_44px_rgba(13,148,136,0.12)]"
                                                        : "border-[#E2E8F0] bg-white/70"
                                                }`}
                                            >
                                                <button
                                                    onClick={() => setOpenNum(isOpen ? null : item.num)}
                                                    aria-expanded={isOpen}
                                                    className="flex w-full items-center gap-4 px-5 py-4 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488]"
                                                    data-testid={`showcase-accordion-${item.num}`}
                                                >
                                                    <FeatureIcon num={item.num} active={isOpen} />
                                                    <span className="flex-1">
                                                        <span className={`block text-sm font-bold ${isOpen ? "text-[#0F172A]" : "text-[#475569]"}`}>
                                                            {item.title}
                                                        </span>
                                                        <span className="mt-1 block text-xs leading-relaxed text-[#64748B]">{item.short}</span>
                                                    </span>
                                                    <span
                                                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${
                                                            isOpen ? "border-[#0D9488]/50 bg-[#0D9488]/10" : "border-[#E2E8F0] bg-[#F8FAFC]"
                                                        }`}
                                                    >
                                                        <Plus
                                                            size={16}
                                                            className={`text-[#0D9488] transition-transform duration-300 ${isOpen ? "rotate-45" : ""}`}
                                                        />
                                                    </span>
                                                </button>
                                                <AnimatePresence initial={false}>
                                                    {isOpen && (
                                                        <motion.div
                                                            initial={{ height: 0, opacity: 0 }}
                                                            animate={{ height: "auto", opacity: 1 }}
                                                            exit={{ height: 0, opacity: 0 }}
                                                            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                                                            className="overflow-hidden"
                                                        >
                                                            <div className="px-5 pb-8">
                                                                <DetailList item={item} f={t.features} />
                                                                <div className="mt-8 flex justify-center">
                                                                    <PhoneCarousel
                                                                        key={`m-showcase-${item.num}`}
                                                                        imgs={item.imgs}
                                                                        alt={item.alt}
                                                                        contain
                                                                        glow={glowCycle[gi % glowCycle.length]}
                                                                        testId={`showcase-phone-mobile-${item.num}`}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </main>
            <Footer t={t} />
        </div>
    );
};
